import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import ButtonCustom from '@/components/common/ui/button';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { useTranslation } from '@/config/i18n';
import useSearch from '@/hooks/useSearch';
import { DATE_FORMAT } from '@/shared/constants';
import {
  MentorFormSearchType,
  mentorSearchFormSchema,
} from '@/shared/schema/mentor';
import { parseQueryValue } from '@/lib/utils';

type Props = {
  onSearch: () => void;
};

const MentorFormSearch: React.FC<Props> = ({ onSearch }) => {
  const [t] = useTranslation('');
  const { handleSearch, queryRaw } = useSearch();

  const formMethods = useForm<MentorFormSearchType>({
    mode: 'onChange',
    resolver: yupResolver(mentorSearchFormSchema),
    shouldFocusError: false,
    defaultValues: {
      keyword: '',
      start_date: null,
      end_date: null,
    },
  });
  const { handleSubmit, getValues, setValue, reset, watch } = formMethods;
  const startDate = watch('start_date');
  const endDate = watch('end_date');

  const getAllFieldNames = () => Object.keys(getValues());

  // Restore the form from the URL so filters survive a reload / back navigation.
  function mapValueFromQuery() {
    getAllFieldNames().forEach((field: any) => {
      const val = queryRaw[field];
      if (!val) return;
      setValue(field, parseQueryValue(field, val));
    });
  }

  useEffect(() => {
    mapValueFromQuery();
  }, []);

  function onSubmit() {
    const { keyword, start_date, end_date } = getValues();

    handleSearch({
      keyword,
      start_date: start_date
        ? dayjs(start_date.toString()).format('YYYY-MM-DD')
        : '',
      end_date: end_date ? dayjs(end_date.toString()).format('YYYY-MM-DD') : '',
    });
    // handleSearch is a no-op when the filters did not change, so notify the
    // list explicitly to make a repeated search still refetch.
    onSearch();
  }

  function handleReset() {
    reset();
    handleSearch({ reset: true });
    onSearch();
  }

  return (
    <FormProvider {...formMethods}>
      <form className="mt-[16px]" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex">
          <div className="w-full max-w-[340px] search-input relative">
            <span className="pi pi-search absolute top-[14px] left-[14px] text-gray-500 text-[15px] z-40" />
            <InputTextCustomForm
              name="keyword"
              className="!pl-[40px]"
              placeholder={t('user_list.keyword_mentor')}
            />
          </div>
          <CalendarCustomForm
            className="date-custom"
            placeholder="----/--/--"
            name="start_date"
            dateFormat={DATE_FORMAT}
            wrapClass="ml-4 w-[160px]"
            showIcon
            maxDate={endDate ? new Date(endDate ?? '') : undefined}
            icon={() => <i className="pi pi-calendar text-[18px]" />}
          />
          <div className="flex-full-center ml-4">~</div>
          <CalendarCustomForm
            className="date-custom"
            placeholder="----/--/--"
            name="end_date"
            dateFormat={DATE_FORMAT}
            wrapClass="ml-4 w-[160px]"
            showIcon
            minDate={startDate ? new Date(startDate ?? '') : undefined}
            icon={() => <i className="pi pi-calendar text-[18px]" />}
          />
          <div className="action flex ml-4">
            <ButtonCustom
              type="button"
              severity="secondary"
              onClick={() => handleReset()}
            >
              <span className="pi pi-refresh" />
            </ButtonCustom>
            <ButtonCustom type="submit" className="ml-2">
              <span className="pi pi-search" />
            </ButtonCustom>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default MentorFormSearch;
